import React, { useCallback, useRef } from 'react'
import styled from 'styled-components'

const ResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -5px;
  bottom: 0;
  width: 10px;
  cursor: col-resize;
  z-index: 1;
  user-select: none;

  &:hover::after,
  &.resizing::after {
    content: '';
    position: absolute;
    right: 5px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #1890ff;
  }
`

const StyledTh = styled.th`
  position: relative;

  &.resizing * {
    pointer-events: none !important;
    user-select: none !important;
  }
`

const GlobalResizingStyle = styled.div`
  table.resizing-table {
    /*table-layout: fixed !important;*/
  }
`

interface ResizableTitleProps extends React.HTMLAttributes<HTMLTableHeaderCellElement> {
  onResize?: (width: number) => void
  onResizeEnd?: (width: number) => void
  width?: number
  minWidth?: number
  maxWidth?: number
}

const ResizableTitle: React.FC<ResizableTitleProps> = ({
  onResize,
  onResizeEnd,
  width,
  minWidth = 50,
  maxWidth = 1000,
  children,
  ...restProps
}) => {
  const handleRef = useRef<HTMLDivElement>(null)
  const thRef = useRef<HTMLTableHeaderCellElement>(null)
  const startXRef = useRef<number>(0)
  const startWidthRef = useRef<number>(0)
  const lockedWidthsRef = useRef<Map<number, number>>(new Map())
  const rafIdRef = useRef<number>(0)
  const pendingWidthRef = useRef<number>(0)
  const tableContainerRef = useRef<HTMLElement | null>(null)
  const didMoveRef = useRef<boolean>(false)

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()

      if (!handleRef.current || !thRef.current) return

      const th = thRef.current
      const table = th.closest('table')
      const columnIndex = Array.from(th.parentElement?.children || []).indexOf(th)

      startXRef.current = e.clientX
      startWidthRef.current = th.offsetWidth

      handleRef.current.classList.add('resizing')
      th.classList.add('resizing')
      didMoveRef.current = false

      if (table) {
        table.classList.add('resizing-table')
        const tableContainer = table.closest('.ant-table-body') || table.closest('.ant-table-content')
        tableContainerRef.current = tableContainer as HTMLElement
      }

      if (table && lockedWidthsRef.current.size === 0) {
        const headerRow = table.querySelector('thead tr')
        if (headerRow) {
          Array.from(headerRow.children).forEach((cell, idx) => {
            const htmlCell = cell as HTMLElement
            lockedWidthsRef.current.set(idx, htmlCell.offsetWidth)
          })
        }
      }

      const applyWidth = (newWidth: number) => {
        if (!table || columnIndex < 0) return

        const actualDelta = newWidth - startWidthRef.current
        const isDecreasing = actualDelta < 0
        const nextColumnIndex = columnIndex + 1
        const hasNextColumn = nextColumnIndex < lockedWidthsRef.current.size

        let totalTableWidth = 0
        const updatedWidths = new Map<number, number>()

        lockedWidthsRef.current.forEach((lockedWidth, idx) => {
          if (idx === columnIndex) {
            updatedWidths.set(idx, newWidth)
            totalTableWidth += newWidth
          } else {
            updatedWidths.set(idx, lockedWidth)
            totalTableWidth += lockedWidth
          }
        })

        const containerWidth = tableContainerRef.current?.clientWidth || 0
        const shouldExpandNext = isDecreasing && hasNextColumn && totalTableWidth < containerWidth

        if (shouldExpandNext) {
          const availableSpace = containerWidth - totalTableWidth
          const nextOriginalWidth = updatedWidths.get(nextColumnIndex) || 0
          const nextNewWidth = nextOriginalWidth + availableSpace
          updatedWidths.set(nextColumnIndex, nextNewWidth)
        }

        const tableWrapper = table.closest('.ant-table-wrapper')
        const headerTable = tableWrapper?.querySelector('.ant-table-header table')
        const bodyTable = tableWrapper?.querySelector('.ant-table-body table')
        const tables = [headerTable, bodyTable].filter(Boolean) as HTMLTableElement[]

        tables.forEach(targetTable => {
          const colgroup = targetTable.querySelector('colgroup')
          const allRows = targetTable.querySelectorAll('tr')

          updatedWidths.forEach((width, idx) => {
            if (colgroup) {
              const col = colgroup.children[idx] as HTMLElement
              if (col) {
                col.style.width = `${width}px`
                col.style.minWidth = `${width}px`
                col.style.maxWidth = `${width}px`
              }
            }

            allRows.forEach(row => {
              const cell = row.children[idx] as HTMLElement
              if (cell) {
                cell.style.width = `${width}px`
                cell.style.minWidth = `${width}px`
                cell.style.maxWidth = `${width}px`
              }
            })
          })
        })

        if (onResize) {
          onResize(newWidth)
        }
      }

      const handleMouseMove = (moveEvent: MouseEvent) => {
        moveEvent.preventDefault()
        moveEvent.stopPropagation()

        didMoveRef.current = true

        const delta = moveEvent.clientX - startXRef.current
        const newWidth = Math.min(Math.max(startWidthRef.current + delta, minWidth), maxWidth)

        pendingWidthRef.current = newWidth

        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current)
        }

        rafIdRef.current = requestAnimationFrame(() => {
          applyWidth(pendingWidthRef.current)
        })
      }

      const handleMouseUp = (upEvent: MouseEvent) => {
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current)
        }

        const didMove = didMoveRef.current

        if (didMove) {
          upEvent.preventDefault()
          upEvent.stopPropagation()

          setTimeout(() => {
            const handleClick = (clickEvent: MouseEvent) => {
              clickEvent.preventDefault()
              clickEvent.stopPropagation()
              clickEvent.stopImmediatePropagation()
              document.removeEventListener('click', handleClick, true)
            }
            document.addEventListener('click', handleClick, true)
          }, 0)
        }

        const finalWidth = pendingWidthRef.current

        if (handleRef.current) {
          handleRef.current.classList.remove('resizing')
        }
        th.classList.remove('resizing')

        if (table) {
          table.classList.remove('resizing-table')
        }

        lockedWidthsRef.current.clear()
        tableContainerRef.current = null
        didMoveRef.current = false

        if (onResizeEnd && didMove) {
          onResizeEnd(finalWidth)
        }

        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [onResize, onResizeEnd, minWidth, maxWidth]
  )

  if (!onResize && !onResizeEnd) {
    return <th {...restProps}>{children}</th>
  }

  return (
    <StyledTh {...restProps} ref={thRef}>
      {children}
      <ResizeHandle ref={handleRef} onMouseDown={handleMouseDown} />
    </StyledTh>
  )
}

export default ResizableTitle
